import { useAccountType } from '@/hooks/useAccountType'
import { useIsOrgAdmin } from '@/hooks/useIsOrgAdmin'
import { useQuery } from '@tanstack/react-query'

import type {
  OrganizationPricing,
  PriceView,
  PricingInterval,
} from '@scrambl/shared'

import { pricingApi } from './pricing-api'

export const organizationPricingQueryKey = (organizationId: string) =>
  ['subscription', 'pricing', organizationId] as const

/**
 * The organization's effective price per plan and interval. Owners and admins
 * only (the endpoint is role-gated) and never requested for personal spaces,
 * which have no billing. Callers fall back to catalog prices while it loads
 * or when it is unavailable.
 */
export function useOrganizationPricing(organizationId: string | undefined) {
  const { isPersonal } = useAccountType()
  const isAdmin = useIsOrgAdmin()
  const enabled = Boolean(organizationId) && !isPersonal && isAdmin

  const query = useQuery({
    queryKey: organizationPricingQueryKey(organizationId ?? 'none'),
    queryFn: () => pricingApi.get(organizationId!),
    enabled,
    staleTime: 60_000,
    retry: false,
  })

  const pricing: OrganizationPricing | null = enabled
    ? (query.data ?? null)
    : null

  const priceFor = (
    planId: string,
    interval: PricingInterval,
  ): PriceView | null =>
    pricing?.plans.find(plan => plan.planId === planId)?.prices[interval] ??
    null

  return { pricing, priceFor, isLoading: enabled && query.isLoading }
}

/** Cents → "$50.40" (whole dollars without decimals: "$72"). */
export function formatPriceCents(cents: number, currency = 'usd'): string {
  const whole = Math.round(cents) % 100 === 0
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100)
}
